import { useState, useRef, useEffect } from 'react'
import { downloadExcelTemplate, type RecurringTemplateOption } from '@/lib/excel/generator'

interface TransactionResult {
  imported: number
  skipped: number
  errors: { row: number; message: string }[]
}

interface RecurringResult {
  created: number
  updated: number
  errors: { row: number; message: string }[]
}

interface ImportResult {
  transactions: TransactionResult
  recurring: RecurringResult
  categoriesCreated: string[]
}

interface DataImportProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface RecurringTemplate {
  id: number
  type: 'income' | 'expense'
  amount: number
  description: string | null
  category_name: string
  family_member_name: string
}

interface Category {
  id: number
  name: string
}

interface FamilyMember {
  id: number
  name: string
}

// API response wrappers
interface RecurringApiResponse {
  templates: RecurringTemplate[]
}

interface CategoriesApiResponse {
  categories: Category[]
}

interface FamilyMembersApiResponse {
  familyMembers: FamilyMember[]
}

export function DataImport({ isOpen, onClose, onSuccess }: DataImportProps) {
  const [file, setFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [parseErrors, setParseErrors] = useState<
    { row: number; field: string; message: string; sheet?: string }[]
  >([])
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Data for Excel template generation
  const [recurringTemplates, setRecurringTemplates] = useState<RecurringTemplateOption[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [familyMembers, setFamilyMembers] = useState<string[]>([])

  // Fetch data needed for Excel template dropdowns when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTemplateData()
      handleReset()
    }
  }, [isOpen])

  async function fetchTemplateData() {
    setIsLoadingData(true)
    try {
      const [recurringRes, categoriesRes, familyMembersRes] = await Promise.all([
        fetch('/api/recurring?active=true'),
        fetch('/api/categories'),
        fetch('/api/family-members')
      ])

      if (recurringRes.ok) {
        const data = await recurringRes.json() as RecurringApiResponse
        setRecurringTemplates(data.templates.map((r) => ({
          id: r.id,
          description: r.description || 'Unnamed',
          type: r.type,
          amount: r.amount,
          categoryName: r.category_name,
          familyMemberName: r.family_member_name
        })))
      }

      if (categoriesRes.ok) {
        const data = await categoriesRes.json() as CategoriesApiResponse
        setCategories(data.categories.map((c) => c.name))
      }

      if (familyMembersRes.ok) {
        const data = await familyMembersRes.json() as FamilyMembersApiResponse
        setFamilyMembers(data.familyMembers.map((fm) => fm.name))
      }
    } catch (err) {
      console.error('Failed to fetch template data:', err)
    } finally {
      setIsLoadingData(false)
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx')) {
        setError('Please select an Excel (.xlsx) file')
        return
      }
      setFile(selectedFile)
      setResult(null)
      setParseErrors([])
      setError('')
    }
  }

  async function handleUpload() {
    if (!file) return

    setIsLoading(true)
    setError('')
    setResult(null)
    setParseErrors([])

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('/api/import-all', {
        method: 'POST',
        body: formData,
      })

      const data = await response.json() as {
        success?: boolean
        result?: ImportResult
        parseErrors?: { row: number; field: string; message: string; sheet?: string }[]
        error?: string
      }

      if (!response.ok) {
        if (data.parseErrors) {
          setParseErrors(data.parseErrors)
        } else {
          setError(data.error || 'Import failed')
        }
        return
      }

      setResult(data.result || null)
      if (data.result) {
        const hasChanges =
          data.result.transactions.imported > 0 ||
          data.result.recurring.created > 0 ||
          data.result.recurring.updated > 0
        if (hasChanges) {
          onSuccess()
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  async function handleDownloadTemplate() {
    if (isLoadingData) return

    await downloadExcelTemplate({
      recurringTemplates,
      categories,
      familyMembers
    })
  }

  function handleReset() {
    setFile(null)
    setResult(null)
    setParseErrors([])
    setError('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  if (!isOpen) return null

  // Calculate totals for display
  const hasTransactions = result && (result.transactions.imported > 0 || result.transactions.skipped > 0)
  const hasRecurring = result && (result.recurring.created > 0 || result.recurring.updated > 0)
  const hasErrors = result && (result.transactions.errors.length > 0 || result.recurring.errors.length > 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-charcoal">
            Import Data
          </h2>
          <button
            onClick={onClose}
            className="text-stone hover:text-charcoal transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="space-y-5">
          {/* Instructions */}
          <div className="bg-mist/50 rounded-lg p-4 text-sm">
            <p className="font-medium text-ocean mb-2">Excel Import</p>
            <p className="text-charcoal/70 text-xs mb-3">
              Import transactions and recurring templates from a single Excel file.
              The template includes sheets for <strong>Transactions</strong>,{' '}
              <strong>Recurring Income</strong>, and <strong>Recurring Expenses</strong>.
            </p>

            <div className="p-3 bg-white rounded-lg border border-forest/20">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">📊</span>
                <span className="font-medium text-forest">Download Template</span>
              </div>
              <p className="text-charcoal/70 text-xs mb-2">
                Get the Excel template with dropdown menus for categories, family members, and more.
              </p>
              <button
                onClick={handleDownloadTemplate}
                disabled={isLoadingData}
                className="text-forest hover:underline disabled:opacity-50 text-sm"
              >
                {isLoadingData ? 'Loading...' : 'Download FamilyBudget-Import-Template.xlsx'}
              </button>
            </div>
          </div>

          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">
              Select Excel File
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-forest/10 file:text-forest hover:file:bg-forest/20 file:cursor-pointer"
            />
            {file && (
              <p className="mt-1 text-xs text-charcoal/60">
                📊 {file.name}
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-danger/10 text-danger px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Parse Errors */}
          {parseErrors.length > 0 && (
            <div className="bg-warning/10 rounded-lg p-4">
              <p className="font-medium text-warning mb-2">
                Validation Errors ({parseErrors.length})
              </p>
              <ul className="text-sm space-y-1 max-h-40 overflow-y-auto">
                {parseErrors.map((err, i) => (
                  <li key={i} className="text-charcoal/70">
                    {err.sheet && <span className="text-xs text-stone">[{err.sheet}]</span>}{' '}
                    Row {err.row}: <span className="font-medium">{err.field}</span> - {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Import Result */}
          {result && (
            <div className="rounded-lg p-4 bg-cream space-y-4">
              <p className="font-medium text-charcoal">Import Complete</p>

              {/* Transactions Summary */}
              {hasTransactions && (
                <div>
                  <p className="text-sm font-medium text-charcoal/80 mb-2">Transactions</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-xl font-bold text-success">{result.transactions.imported}</p>
                      <p className="text-xs text-sage">Imported</p>
                    </div>
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-xl font-bold text-warning">{result.transactions.skipped}</p>
                      <p className="text-xs text-sage">Duplicates</p>
                    </div>
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-xl font-bold text-danger">{result.transactions.errors.length}</p>
                      <p className="text-xs text-sage">Errors</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recurring Summary */}
              {hasRecurring && (
                <div>
                  <p className="text-sm font-medium text-charcoal/80 mb-2">Recurring Templates</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-xl font-bold text-success">{result.recurring.created}</p>
                      <p className="text-xs text-sage">Created</p>
                    </div>
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-xl font-bold text-ocean">{result.recurring.updated}</p>
                      <p className="text-xs text-sage">Updated</p>
                    </div>
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-xl font-bold text-danger">{result.recurring.errors.length}</p>
                      <p className="text-xs text-sage">Errors</p>
                    </div>
                  </div>
                </div>
              )}

              {/* New Categories */}
              {result.categoriesCreated.length > 0 && (
                <div className="text-xs text-charcoal/70 bg-white rounded-lg p-2">
                  <span className="font-medium">New categories created: </span>
                  {result.categoriesCreated.join(', ')}
                </div>
              )}

              {/* Errors Detail */}
              {hasErrors && (
                <div className="bg-white rounded-lg p-3">
                  <p className="text-sm font-medium text-charcoal mb-2">Errors</p>
                  <ul className="text-xs text-charcoal/70 max-h-32 overflow-y-auto space-y-1">
                    {result.transactions.errors.map((err, i) => (
                      <li key={`t-${i}`}>
                        <span className="text-stone">[Transaction]</span> Row {err.row}: {err.message}
                      </li>
                    ))}
                    {result.recurring.errors.map((err, i) => (
                      <li key={`r-${i}`}>
                        <span className="text-stone">[Recurring]</span> Row {err.row}: {err.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* No data message */}
              {!hasTransactions && !hasRecurring && (
                <p className="text-sm text-charcoal/60 text-center">
                  No data was imported. Make sure your Excel file has data in the expected sheets.
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {result ? (
              <>
                <button
                  onClick={handleReset}
                  className="flex-1 py-2 px-4 border border-stone/30 rounded-lg text-charcoal hover:bg-cream transition-colors"
                >
                  Import More
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-2 px-4 bg-forest text-white rounded-lg hover:bg-forest-dark transition-colors"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="flex-1 py-2 px-4 border border-stone/30 rounded-lg text-charcoal hover:bg-cream transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!file || isLoading}
                  className="flex-1 py-2 px-4 bg-forest text-white rounded-lg hover:bg-forest-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Importing...' : 'Import'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
